import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useState} from 'react';
import {gqlErrorToast, showSuccessToast} from '@/libs/api/errorHandler';
import {ProductsPaginatedTake} from '@/libs/constants/common.constants';
import {useTranslations} from 'next-intl';

export function useBaseQuery(query, variables, callback) {
	const t = useTranslations('error.message');
	const [data, setData] = useState(null);

	const { loading, refetch } = useQuery(query, {
		variables,
		fetchPolicy: 'no-cache',
		onCompleted: (result) => {
			const data = Object.keys(result);
			let parsedData;
			
			if (data.length === 1) parsedData = result[data[0]];
			else parsedData = data.map(d => result[d]);
			
			if (typeof callback === 'function') {
				parsedData = callback(parsedData);
			}
			setData(parsedData);
		},
		onError: async (e) => {
			await gqlErrorToast(e, t);
		}
	});
	return { data, loading, refetch };
}

export function useBaseLazyQuery(query) {
	const t = useTranslations('error.message');
	const [data, setData] = useState(null);
	
	const [fetchData, { loading, refetch }] = useLazyQuery(query, {
		fetchPolicy: 'no-cache',
		onCompleted: (result) => {
			const keys = Object.keys(result);
			if (keys.length === 1) setData(result[keys[0]]);
			else setData(keys.map(key => result[key]));
		},
		onError: async (e) => {
			await gqlErrorToast(e, t);
		}
	});
	
	return { data, loading, fetchData, refetch };
}

export function usePaginatedQuery(query, variables, options, initData = []) {
	const t = useTranslations('error.message');
	
	const [items, setItems] = useState(initData);
	const [hasMore, setHasMore] = useState(!!options?.hasMore);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(options?.total ?? 0);
	
	const { fetchMore } = useQuery(query, {
		variables,
		skip: options?.skipFirst,
		fetchPolicy: 'network-only',
		onError: async (e) => {
			await gqlErrorToast(e, t);
		}
	});
	
	const fetchMoreData = async (extraVar = {}, callback) => {
		if (!hasMore) return;
		
		const { data: newData } = await fetchMore({
			variables: {
				...variables,
				...extraVar,
				skip: items.length
			},
		});
		console.log('fetchMoreData', {
			...variables,
			...extraVar,
			skip: items.length
		}, newData)
		const keys = Object.keys(newData);
		const data = newData[keys[0]];
		
		if (typeof callback === 'function') {
			callback(data.items);
		}
		
		setItems(prev => [...prev, ...data.items]);
		setHasMore(data.hasMore);
		setPage(prev => prev + 1);
		setTotal(data.total);
	}
	
	const refetchData = async (extraVar = {}, callback) => {
		const { data: newData } = await fetchMore({
			variables: {
				...variables,
				...extraVar
			},
		});
		console.log('refetchData', variables,extraVar, newData)
		const keys = Object.keys(newData);
		const data = newData[keys[0]];
		
		if (typeof callback === 'function') {
			callback(data.items);
		}
		
		setItems(data.items);
		setHasMore(data.hasMore);
		setPage(Math.floor(items.length / ProductsPaginatedTake));
		setTotal(data.total);
	}
	
	const resetPagination = () => {
		setItems([]);
		setHasMore(!!options?.hasMore);
		setPage(1);
		setTotal(options?.total ?? 0);
	}
	
	return { items, setItems, fetchMoreData, hasMore, page, total, refetchData, resetPagination };
}

export function useBaseMutation(mutation, {
	variables = {},
	toastOnComplete = { toast: false, message: '' },
	isFile = false
}) {
	const t = useTranslations('error.message');
	const [mutate] = useMutation(mutation, {
		variables,
		fetchPolicy: 'network-only',
		context: {
			hasUpload: isFile
		},
		onCompleted: async () => {
			if (toastOnComplete?.toast) {
				await showSuccessToast(toastOnComplete?.message || t('successMessageSent'));
			}
		},
		onError: async (e) => {
			await gqlErrorToast(e, t);
		}
	});
	const parseData = (result) => {
		const data = Object.keys(result);
		return result[data[0]];
	}
	return { mutate, parseData };
}
